<template>
  <div class="betConfirmationModal" :class="{ 'ru-locale': $i18n.locale === 'ru' }">
    <div class="header">
      <p class="title">{{ $t('flipCoinBetConfirmation') }}</p>
      <img class="close" src="/img/icons/bone_close.svg" alt="close" @click="getOut" />
    </div>
    <div class="body">
      <p class="owned">
        {{ `${$t('appStatisticOwnedToken')}:` }}
        <img :src="tokenImage" alt="token image" />
        <span>{{ formatAmountFloor(available || 0) }}</span>
      </p>
      <p class="youBet">{{ $t('flipCoinYouBet') }}:</p>
      <div class="token-balance">
        <img :src="tokenImage" alt="token image" />
        <span class="token-amount">{{
          BigNumber(props.gameData?.betAmount || 0).toFormat(0, {
            groupSize: 3,
            groupSeparator: ' ',
            decimalSeparator: '.'
          })
        }}</span>
      </div>
      <TavernFlipCoinPointsForBet
        style="width: max-content"
        :points-for-bet="pointsForBet"
        :label-text="$t('tavernPointsForBet')"
      />

      <div class="placeBetWrapper">
        <el-tooltip :disabled="hasLeastTokens" popper-class="popper-default" placement="left">
          <template #content>
            <div v-html="$t('flipCoinNotEnoughTokensToPlaceBet')"></div>
          </template>
          <div>
            <TavernFlipCoinBigButton
              class="placeBetBtn"
              :label="$t('confirmBetButton')"
              :disabled="!hasLeastTokens"
              @click="$emit('confirm-bet')"
            />
          </div>
        </el-tooltip>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import { type FlipCoinGame } from '~/types/FlipCoin';
import tokens from '~/public/img/tokens';
import type { Tokens } from '~/utils/constants';
import BigNumber from 'bignumber.js';
import { parseUnits } from 'ethers';
import type { IFlipCoinTokenConfig } from '~/types/apiService';

const emit = defineEmits(['get-out', 'confirm-bet']);
const { blockchain } = useEnvs();
const { address } = useWeb3ModalAccount();
const { availableTokens } = useTokensReader();
const { getLastAddedSPTimeAndMaxSP } = useFlipCoinMethods();
const { data: tokensConfig } = useNuxtData<IFlipCoinTokenConfig[]>('tokensList');

const exchangeToken = computed(
  () => tokensConfig.value?.find((token) => token.tokenAddress === props.gameData?.tokenAddress)!
);
const available = ref<string | null>(null);

const props = defineProps<{
  gameData: FlipCoinGame | null;
}>();

const lastSPInCurrentPeriod = asyncComputed(
  async () =>
    props.gameData?.tokenAddress ? await getLastAddedSPTimeAndMaxSP(props.gameData?.tokenAddress) : [0, 0, 0],
  [0, 0, 0]
);

const pointsForBet = computed(() => {
  const totalAmountOfBetsInCurrentPeriod = BigNumber(
    parseUnits(props.gameData?.betAmount?.toString() || '0', 'ether').toString()
  )
    .plus(lastSPInCurrentPeriod.value[0].toString())
    .toNumber();

  return getSPAmount(
    totalAmountOfBetsInCurrentPeriod,
    exchangeToken.value.seasonPointsMultiplier,
    parseInt(lastSPInCurrentPeriod.value[2].toString())
  );
});

watch(address, (newAddress, oldAddress) => {
  if (oldAddress && newAddress !== oldAddress) {
    getOut();
  }
});

onMounted(async () => {
  available.value = await availableTokens(address.value!, props.gameData?.tokenAddress!);
});

const tokenImage = computed(() => {
  const contract = tokenMetaData(blockchain.contracts, props.gameData?.tokenAddress!).name.contract;
  return tokens[contract as Tokens].toString();
});

const hasLeastTokens = computed(() => {
  if (!available.value) return false;

  return Number(props.gameData?.betAmount!) <= Number(available.value);
});

const getOut = () => {
  emit('get-out');
};
</script>

<style lang="scss" scoped>
.betConfirmationModal {
  width: 461px;
  border-radius: 12px;
  background: linear-gradient(to bottom, #4e3127, #000000);
  margin: 0 auto;
  padding-top: 7px;
  margin-top: 7%;
  z-index: 9999;
  position: absolute;
  left: 25%;
  border: 1px solid #483b20;

  @media screen and (max-width: 479px) {
    width: 99vw;
    margin-top: 0;
    left: 0;
  }
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: -5px 20px;

  .title {
    font-size: 32px;
    font-weight: 700;
    color: #fff;
    max-width: 80%;
    margin: 0 auto;
    font-family: Grenze, sans-serif;
    text-align: center;
  }

  .close {
    width: 20px;
    height: 20px;
    cursor: pointer;
  }
}

.body {
  text-align: center;
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  align-items: center;

  .owned {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    color: #a2a2a2;
    font-weight: 700;

    img {
      width: 40px;
      height: 40px;
      margin: 0px 8px;
    }

    span {
      color: #fff;
    }
  }

  .youBet {
    color: #fff;
    font-size: 24px;
    font-weight: 500;
  }

  .token-balance {
    display: inline-flex;
    align-items: center;
    background-color: #1b110c;
    padding: 8px 35px;
    border-radius: 14px;
    border: 1px solid rgba(255, 255, 255, 0.05);
    box-shadow: inset 0 0 4px rgba(255, 255, 255, 0.04);
    width: max-content;

    img {
      width: 28px;
      height: 28px;
    }

    .token-amount {
      font-weight: 700;
      font-size: 24px;
      color: #fff;
      padding-left: 5px;
    }
  }

  .placeBetWrapper {
    display: flex;
    margin-bottom: 15px;

    .el-tooltip__trigger {
      margin: 0 auto;
    }

    .placeBetBtn {
      width: 100%;
    }
  }
}

.ru-locale {
  .title {
    font-size: 28px;
  }
}
</style>
